/**
 * Dynamic Sheet Manager - Automatically discovers and manages Google Sheets based on Unity table names
 *
 * This service eliminates the need for predefined sheet mappings by:
 * - Automatically finding existing sheets that match Unity table names
 * - Creating new sheets within a master spreadsheet when needed
 * - Caching sheet mappings during the session
 * - Handling key columns and language columns dynamically
 * - Providing a simple, automatic API for developers
 */

import { googleSheetsService } from '../google-sheets-service';
import { GOOGLE_CONFIG } from '../app-config';
import type { BatchUpdateEntry } from '../google-sheets-service';

const DEFAULT_LANGUAGE_PLACEHOLDER = 'Default Language';

/**
 * Information about a discovered or created sheet
 */
export interface SheetInfo {
  /** The Unity table name this sheet represents */
  tableName: string;
  /** Google Sheets spreadsheet ID */
  spreadsheetId: string;
  /** Sheet name within the spreadsheet */
  sheetName: string;
  /** Sheet ID for API operations */
  sheetId: number;
  /** Whether this sheet was created during this session */
  wasCreated: boolean;
  /** Column index for localization keys (0-based) */
  keyColumnIndex: number;
  /** Available language columns mapped by language name */
  languageColumns: Map<string, number>;
  /** Last time this sheet info was updated */
  lastUpdated: Date;
}

/**
 * Data structure for table data
 */
export interface TableData {
  /** Headers from the sheet */
  headers: string[];
  /** All rows of data (excluding header) */
  rows: string[][];
  /** Map of keys to their row indices for quick lookup */
  keyToRowIndex: Map<string, number>;
}

/**
 * Update operation for a specific table
 */
export interface TableUpdate {
  /** Localization key */
  key: string;
  /** Language being updated */
  language: string;
  /** New text value */
  value: string;
}

/**
 * Result of a sheet operation
 */
export interface SheetOperationResult {
  /** Whether the operation succeeded */
  success: boolean;
  /** Number of entries affected */
  affectedCount: number;
  /** Error message if operation failed */
  error?: string;
  /** Additional details */
  details?: string;
}

/**
 * Get master spreadsheet and table-to-sheet mappings
 */
const MASTER_SPREADSHEET = (GOOGLE_CONFIG as any).masterSpreadsheet || '';
const TABLE_SHEETS = (GOOGLE_CONFIG as any).tableSheets as Record<string, { sheetId: number; sheetName: string }> || {};

/**
 * Dynamic Sheet Manager Service
 */
class DynamicSheetManagerService {
  private static instance: DynamicSheetManagerService;

  /** Cache of discovered sheet information */
  private sheetCache = new Map<string, SheetInfo>();

  /** Cache of table data to reduce redundant reads */
  private tableDataCache = new Map<string, { data: TableData; timestamp: number }>();

  /** Track access errors for tables */
  private accessErrors = new Map<string, string>();

  /** Cache TTL in milliseconds (30 minutes) */
  private readonly CACHE_TTL = 30 * 60 * 1000;

  /** Table data cache TTL (30 seconds) */
  private readonly TABLE_DATA_CACHE_TTL = 30 * 1000;

  /** Whether initial discovery has been performed */
  private discoveryComplete = false;

  private constructor() {}

  public static getInstance(): DynamicSheetManagerService {
    if (!DynamicSheetManagerService.instance) {
      DynamicSheetManagerService.instance = new DynamicSheetManagerService();
    }
    return DynamicSheetManagerService.instance;
  }

  /**
   * Get the spreadsheet ID for a specific table (always returns master spreadsheet)
   */
  private getSpreadsheetIdForTable(tableName: string): string | null {
    if (!MASTER_SPREADSHEET || !TABLE_SHEETS[tableName]) {
      console.warn(`[DynamicSheetManager] No spreadsheet configured for table: ${tableName}`);
      return null;
    }
    console.log(`[DynamicSheetManager] Using master spreadsheet for ${tableName}: ${MASTER_SPREADSHEET}`);
    return MASTER_SPREADSHEET;
  }

  /**
   * Get the sheet info (name and ID) for a specific table
   */
  private getSheetInfoForTable(tableName: string): { sheetName: string; sheetId: number } | null {
    return TABLE_SHEETS[tableName] || null;
  }

  /**
   * Check if a table has access errors
   */
  public hasAccessError(tableName: string): boolean {
    const normalizedTableName = this.normalizeTableName(tableName);
    return this.accessErrors.has(normalizedTableName);
  }

  /**
   * Get the access error message for a table
   */
  public getAccessError(tableName: string): string | undefined {
    const normalizedTableName = this.normalizeTableName(tableName);
    return this.accessErrors.get(normalizedTableName);
  }

  /**
   * Check if a table has no configured spreadsheet
   */
  public hasNoConfiguration(tableName: string): boolean {
    return !(MASTER_SPREADSHEET && TABLE_SHEETS[tableName]);
  }

  /**
   * Get or create a sheet for the specified Unity table name
   * This is the main entry point - handles everything automatically
   */
  public async getOrCreateSheet(tableName: string): Promise<SheetInfo> {
    console.log(`[DynamicSheetManager] Getting or creating sheet for table: ${tableName}`);

    // Normalize the table name for consistent lookup
    const normalizedTableName = this.normalizeTableName(tableName);
    console.log(`[DynamicSheetManager] Normalized table name: ${tableName} -> ${normalizedTableName}`);

    // Check cache first
    const cached = this.sheetCache.get(normalizedTableName);
    if (cached && this.isCacheValid(cached)) {
      console.log(`[DynamicSheetManager] Found cached sheet info for: ${normalizedTableName}`, {
        sheetName: cached.sheetName,
        sheetId: cached.sheetId,
        spreadsheetId: cached.spreadsheetId
      });
      return cached;
    }

    // Ensure we've discovered existing sheets
    if (!this.discoveryComplete) {
      await this.discoverExistingSheets();
    }

    // Check cache again after discovery
    const afterDiscovery = this.sheetCache.get(normalizedTableName);
    if (afterDiscovery && this.isCacheValid(afterDiscovery)) {
      return afterDiscovery;
    }

    // Get the configured spreadsheet for this table
    const spreadsheetId = this.getSpreadsheetIdForTable(tableName);
    if (!spreadsheetId) {
      // No spreadsheet configured for this table
      throw new Error(`No spreadsheet configured for table: ${tableName}. Please add it to the configuration.`);
    }

    // Try to find the sheet in the table's spreadsheet
    try {
      console.log(`[DynamicSheetManager] Checking spreadsheet for ${tableName}`);
      const sheetInfo = await this.discoverSheetInSpreadsheet(tableName, spreadsheetId);
      if (sheetInfo) {
        this.sheetCache.set(normalizedTableName, sheetInfo);
        return sheetInfo;
      }

      // Sheet doesn't exist in the spreadsheet, create it
      console.log(`[DynamicSheetManager] Creating new sheet for table: ${normalizedTableName}`);
      return await this.createSheet(normalizedTableName, spreadsheetId);
    } catch (error: any) {
      // Check if it's an access error
      if (error?.status === 403 || error?.message?.includes('forbidden') || error?.message?.includes('permission')) {
        console.error(`[DynamicSheetManager] Access denied to spreadsheet for table ${tableName}`);
        throw new Error(`No access to spreadsheet for table: ${tableName}. Please request access to spreadsheet ID: ${spreadsheetId}`);
      }
      throw error;
    }
  }

  /**
   * Get sheet information for a table (does not create if missing)
   */
  public async getSheetForTable(tableName: string): Promise<SheetInfo | null> {
    console.log(`[DynamicSheetManager] Getting sheet info for table: ${tableName}`);

    // Ensure discovery is complete
    if (!this.discoveryComplete) {
      await this.discoverExistingSheets();
    }

    const normalizedTableName = this.normalizeTableName(tableName);
    const cached = this.sheetCache.get(normalizedTableName);
    return (cached && this.isCacheValid(cached)) ? cached : null;
  }

  /**
   * Discover a specific sheet in a given spreadsheet
   */
  private async discoverSheetInSpreadsheet(tableName: string, spreadsheetId: string): Promise<SheetInfo | null> {
    try {
      const metadata = await googleSheetsService.getSheetMetadata(spreadsheetId);

      // If using new config, look for specific sheet by name or ID
      const configuredSheet = this.getSheetInfoForTable(tableName);

      if (configuredSheet) {
        // Find sheet by ID or name from configuration
        for (const sheet of metadata.sheets) {
          if (sheet.sheetId === configuredSheet.sheetId || sheet.title === configuredSheet.sheetName) {
            const sheetInfo = await this.analyzeSheet(sheet.title, sheet.sheetId, false, spreadsheetId);
            if (sheetInfo) {
              sheetInfo.tableName = tableName;
              return sheetInfo;
            }
          }
        }
      }

      // Fallback: look for sheet with matching name (old behavior)
      for (const sheet of metadata.sheets) {
        if (sheet.title === tableName || sheet.title === 'Sheet1' || metadata.sheets.indexOf(sheet) === 0) {
          const sheetInfo = await this.analyzeSheet(sheet.title, sheet.sheetId, false, spreadsheetId);
          if (sheetInfo) {
            sheetInfo.tableName = tableName;
            return sheetInfo;
          }
        }
      }
    } catch (error) {
      console.error(`[DynamicSheetManager] Failed to discover sheet in spreadsheet ${spreadsheetId}:`, error);
    }
    return null;
  }

  /**
   * Discover all existing sheets in master spreadsheet
   */
  public async discoverExistingSheets(): Promise<void> {
    console.log(`[DynamicSheetManager] Discovering existing sheets in master spreadsheet`);

    if (!MASTER_SPREADSHEET || Object.keys(TABLE_SHEETS).length === 0) {
      console.warn(`[DynamicSheetManager] No master spreadsheet configured`);
      this.discoveryComplete = true;
      return;
    }

    console.log(`[DynamicSheetManager] Using master spreadsheet: ${MASTER_SPREADSHEET}`);

    for (const [tableName, sheetConfig] of Object.entries(TABLE_SHEETS)) {
      try {
        const sheetInfo = await this.discoverSheetInSpreadsheet(tableName, MASTER_SPREADSHEET);
        if (sheetInfo) {
          const normalizedTableName = this.normalizeTableName(tableName);
          this.sheetCache.set(normalizedTableName, sheetInfo);
          console.log(`[DynamicSheetManager] Discovered table ${tableName} in master spreadsheet (sheet: ${sheetConfig.sheetName}, gid: ${sheetConfig.sheetId})`);
        }
      } catch (error: any) {
        if (error?.status === 403) {
          console.warn(`[DynamicSheetManager] No access to master spreadsheet for table ${tableName}`);
          const normalizedTableName = this.normalizeTableName(tableName);
          this.accessErrors.set(normalizedTableName, `No access to master spreadsheet: ${MASTER_SPREADSHEET}`);
        } else {
          console.error(`[DynamicSheetManager] Failed to discover sheet for table ${tableName}:`, error);
        }
      }
    }

    this.discoveryComplete = true;
    console.log(`[DynamicSheetManager] Discovery complete. Found ${this.sheetCache.size} accessible tables`);
  }

  /**
   * Create a new sheet for the specified table name
   */
  public async createSheet(tableName: string, spreadsheetId?: string): Promise<SheetInfo> {
    console.log(`[DynamicSheetManager] Creating new sheet for table: ${tableName}`);

    // Get the spreadsheet ID for this table if not provided
    const targetSpreadsheetId = spreadsheetId || this.getSpreadsheetIdForTable(tableName);
    if (!targetSpreadsheetId) {
      throw new Error(`No spreadsheet configured for table: ${tableName}`);
    }

    try {
      // Use the table name directly as the sheet name
      const sheetName = tableName;

      // Create the sheet using the Sheets API
      const response = await (window as any).gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId: targetSpreadsheetId,
        resource: {
          requests: [{
            addSheet: {
              properties: {
                title: sheetName
              }
            }
          }]
        }
      });

      const newSheet = response.result.replies[0].addSheet;
      const sheetId = newSheet.properties.sheetId;

      console.log(`[DynamicSheetManager] Created sheet "${sheetName}" with ID: ${sheetId}`);

      // Set up the sheet with proper headers
      await this.setupSheetHeaders(targetSpreadsheetId, sheetName);

      // Analyze the newly created sheet to get full info
      const sheetInfo = await this.analyzeSheet(sheetName, sheetId, true);
      if (!sheetInfo) {
        throw new Error('Failed to analyze newly created sheet');
      }

      // Cache the result using normalized table name
      const normalizedTableName = this.normalizeTableName(tableName);
      this.sheetCache.set(normalizedTableName, sheetInfo);

      console.log(`[DynamicSheetManager] Successfully created and cached sheet for table: ${tableName}`);
      return sheetInfo;

    } catch (error) {
      console.error(`[DynamicSheetManager] Failed to create sheet for table ${tableName}:`, error);
      throw new Error(`Failed to create sheet: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Fetch all data for a specific table
   */
  public async getTableDataFromSheet(tableName: string): Promise<TableData | null> {
    console.log(`[DynamicSheetManager] Fetching data for table: ${tableName}`);

    const normalizedTableName = this.normalizeTableName(tableName);
    const cachedData = this.tableDataCache.get(normalizedTableName);
    if (cachedData && Date.now() - cachedData.timestamp < this.TABLE_DATA_CACHE_TTL) {
      console.log(`[DynamicSheetManager] Returning cached data for table: ${tableName}`);
      return cachedData.data;
    }

    const sheetInfo = await this.getSheetForTable(tableName);
    if (!sheetInfo) {
      console.log(`[DynamicSheetManager] No sheet found for table: ${tableName}`);
      return null;
    }

    try {
      const values = await googleSheetsService.getSheetValues(
        sheetInfo.spreadsheetId,
        sheetInfo.sheetName
      );

      if (!values || values.length === 0) {
        console.log(`[DynamicSheetManager] Sheet is empty for table: ${tableName}`);
        const emptyData: TableData = {
          headers: [],
          rows: [],
          keyToRowIndex: new Map()
        };
        this.tableDataCache.set(normalizedTableName, {
          data: emptyData,
          timestamp: Date.now()
        });
        return emptyData;
      }

      const headers = values[0] || [];
      const rows = values.slice(1);
      const keyToRowIndex = new Map<string, number>();

      // Build key-to-row index mapping
      if (sheetInfo.keyColumnIndex >= 0) {
        rows.forEach((row, index) => {
          const key = row[sheetInfo.keyColumnIndex];
          if (key && key.trim()) {
            keyToRowIndex.set(key.trim(), index);
          }
        });
      }

      console.log(`[DynamicSheetManager] Fetched ${rows.length} rows for table: ${tableName}`);

      const tableData: TableData = {
        headers,
        rows,
        keyToRowIndex
      };

      this.tableDataCache.set(normalizedTableName, {
        data: tableData,
        timestamp: Date.now()
      });

      return tableData;

    } catch (error) {
      console.error(`[DynamicSheetManager] Failed to fetch data for table ${tableName}:`, error);
      return null;
    }
  }

  /**
   * Fetch template values from Google Sheets for multiple keys across multiple tables
   * @param requests - Array of {tableName, key, language} objects
   * @returns Map with composite key "tableName|key|language" -> template value
   */
  public async fetchTemplateValuesByKeys(
    requests: Array<{ tableName: string; key: string; language: string }>
  ): Promise<Map<string, string>> {
    console.log(`[DynamicSheetManager] Fetching template values for ${requests.length} keys`);

    const resultMap = new Map<string, string>();

    if (requests.length === 0) {
      console.log(`[DynamicSheetManager] No requests to process`);
      return resultMap;
    }

    // Group requests by tableName for efficient processing
    const requestsByTable = new Map<string, Array<{ key: string; language: string }>>();

    for (const request of requests) {
      if (!requestsByTable.has(request.tableName)) {
        requestsByTable.set(request.tableName, []);
      }
      requestsByTable.get(request.tableName)!.push({
        key: request.key,
        language: request.language
      });
    }

    console.log(`[DynamicSheetManager] Grouped requests into ${requestsByTable.size} tables`);

    // Process each table
    for (const [tableName, tableRequests] of requestsByTable.entries()) {
      console.log(`[DynamicSheetManager] Processing ${tableRequests.length} requests for table: ${tableName}`);

      try {
        // Fetch the full table data
        const tableData = await this.getTableDataFromSheet(tableName);

        if (!tableData) {
          console.warn(`[DynamicSheetManager] Could not fetch data for table: ${tableName}`);
          continue;
        }

        const sheetInfo = await this.getSheetForTable(tableName);
        if (!sheetInfo) {
          console.warn(`[DynamicSheetManager] Could not get sheet info for table: ${tableName}`);
          continue;
        }

        // Track statistics
        let foundCount = 0;
        let notFoundCount = 0;
        const missingKeys: string[] = [];

        // Process each request for this table
        for (const request of tableRequests) {
          const compositeKey = `${tableName}|${request.key}|${request.language}`;

          // Find the row for this key using the keyToRowIndex map
          const rowIndex = tableData.keyToRowIndex.get(request.key);

          if (rowIndex === undefined) {
            console.warn(`[DynamicSheetManager] Key "${request.key}" not found in table ${tableName}`);
            notFoundCount++;
            missingKeys.push(request.key);
            continue;
          }

          // Find the column for this language
          const columnMapping = GOOGLE_CONFIG.languages?.columnMapping as Record<string, string> | undefined;
          const mappedColumnName = columnMapping?.[request.language];
          const columnNameToFind = mappedColumnName || request.language;
          const languageKey = columnNameToFind.toLowerCase();

          const columnIndex = sheetInfo.languageColumns.get(languageKey);

          if (columnIndex === undefined) {
            console.warn(`[DynamicSheetManager] Language "${request.language}" (mapped to "${columnNameToFind}") not found in table ${tableName}`);
            notFoundCount++;
            continue;
          }

          // Extract the template value from the sheet data
          const row = tableData.rows[rowIndex];
          const templateValue = row?.[columnIndex] || '';

          // Store in the result map
          resultMap.set(compositeKey, templateValue);
          foundCount++;

          console.log(`[DynamicSheetManager] Found template value for key "${request.key}" in table ${tableName}: "${templateValue.substring(0, 50)}${templateValue.length > 50 ? '...' : ''}"`);
        }

        // Log statistics for this table
        console.log(`[DynamicSheetManager] Table "${tableName}" statistics: ${foundCount} found, ${notFoundCount} not found`);
        if (missingKeys.length > 0) {
          console.log(`[DynamicSheetManager] Missing keys in table "${tableName}": ${missingKeys.join(', ')}`);
        }

      } catch (error) {
        console.error(`[DynamicSheetManager] Error processing table ${tableName}:`, error);
        // Continue processing other tables even if one fails
      }
    }

    // Log overall statistics
    console.log(`[DynamicSheetManager] Template fetch complete: ${resultMap.size} values fetched out of ${requests.length} requests`);

    return resultMap;
  }

  /**
   * Update data in a specific table
   */
  public async updateTableData(tableName: string, updates: TableUpdate[]): Promise<SheetOperationResult> {
    console.log(`[DynamicSheetManager] Updating ${updates.length} entries in table: ${tableName}`);

    if (updates.length === 0) {
      return {
        success: true,
        affectedCount: 0,
        details: 'No updates to apply'
      };
    }

    try {
      const sheetInfo = await this.getOrCreateSheet(tableName);
      const tableData = await this.getTableDataFromSheet(tableName);

      if (!tableData) {
        throw new Error('Could not fetch table data');
      }

      const batchUpdates: BatchUpdateEntry[] = [];

      console.log(`[DynamicSheetManager] Processing ${updates.length} updates for table ${tableName}`);
      console.log(`[DynamicSheetManager] Available keys in sheet:`, Array.from(tableData.keyToRowIndex.keys()));

      // Track missing keys for error reporting
      const missingKeys: string[] = [];

      for (const update of updates) {
        // Find the row for this key
        const rowIndex = tableData.keyToRowIndex.get(update.key);

        if (rowIndex === undefined) {
          console.error(`[DynamicSheetManager] Key "${update.key}" not found in table ${tableName}. Sheet may not be properly configured.`);
          missingKeys.push(update.key);
          continue;
        }

        // Find the column for this language using the mapping from config
        const columnMapping = GOOGLE_CONFIG.languages?.columnMapping as Record<string, string> | undefined;
        const mappedColumnName = columnMapping?.[update.language];
        const columnNameToFind = mappedColumnName || update.language;
        const languageKey = columnNameToFind.toLowerCase();

        console.log(`[DynamicSheetManager] Looking for language column: "${update.language}" -> "${columnNameToFind}" -> "${languageKey}" in columns:`, Array.from(sheetInfo.languageColumns.keys()));
        const columnIndex = sheetInfo.languageColumns.get(languageKey);
        if (columnIndex === undefined) {
          console.warn(`[DynamicSheetManager] Language "${update.language}" (mapped to "${columnNameToFind}") not found in table ${tableName}, skipping`);
          continue;
        }

        // Update the in-memory table data immediately so cached reads stay in sync
        if (!tableData.rows[rowIndex]) {
          tableData.rows[rowIndex] = [];
        }
        const targetRow = tableData.rows[rowIndex];
        while (targetRow.length <= columnIndex) {
          targetRow.push('');
        }
        targetRow[columnIndex] = update.value;

        batchUpdates.push({
          row: rowIndex + 2, // +1 for header, +1 for 1-based indexing
          col: columnIndex,
          value: update.value
        });
      }

      if (batchUpdates.length === 0) {
        const errorMsg = missingKeys.length > 0
          ? `Sheet not configured properly. Missing keys: ${missingKeys.join(', ')}`
          : 'No valid updates to apply';

        console.warn(`[DynamicSheetManager] No valid updates for table ${tableName}: ${errorMsg}`);

        return {
          success: missingKeys.length === 0,
          affectedCount: 0,
          details: errorMsg,
          error: missingKeys.length > 0 ? errorMsg : undefined
        };
      }

      // Apply the updates
      await googleSheetsService.batchUpdateSheet(
        sheetInfo.spreadsheetId,
        sheetInfo.sheetName,
        batchUpdates
      );

      const normalizedTableName = this.normalizeTableName(tableName);
      const cachedEntry = this.tableDataCache.get(normalizedTableName);
      if (cachedEntry) {
        cachedEntry.timestamp = Date.now();
      } else {
        this.tableDataCache.set(normalizedTableName, {
          data: tableData,
          timestamp: Date.now()
        });
      }

      console.log(`[DynamicSheetManager] Successfully updated ${batchUpdates.length} entries in table: ${tableName}`);

      return {
        success: true,
        affectedCount: batchUpdates.length,
        details: `Updated ${batchUpdates.length} entries`
      };

    } catch (error) {
      console.error(`[DynamicSheetManager] Failed to update table ${tableName}:`, error);

      // Provide detailed error message based on error type
      let errorMessage = 'Unknown error';
      if (error instanceof Error) {
        if (error.message.includes('No spreadsheet configured')) {
          errorMessage = error.message;
        } else if (error.message.includes('No access to spreadsheet')) {
          errorMessage = error.message;
        } else if (error.message.includes('403') || error.message.includes('permission')) {
          const spreadsheetId = this.getSpreadsheetIdForTable(tableName);
          errorMessage = `No write access to table "${tableName}" (spreadsheet ${spreadsheetId}). Please request edit permissions.`;
        } else if (error.message.includes('404')) {
          const spreadsheetId = this.getSpreadsheetIdForTable(tableName);
          errorMessage = `Spreadsheet for table "${tableName}" not found (ID: ${spreadsheetId}). Please check configuration.`;
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        affectedCount: 0,
        error: errorMessage
      };
    }
  }

  /**
   * Get a list of all discovered tables
   */
  public async getAllTables(): Promise<SheetInfo[]> {
    if (!this.discoveryComplete) {
      await this.discoverExistingSheets();
    }

    return Array.from(this.sheetCache.values())
      .filter(sheet => this.isCacheValid(sheet))
      .sort((a, b) => a.tableName.localeCompare(b.tableName));
  }

  /**
   * Clear the cache for a specific table or all tables
   */
  public clearCache(tableName?: string): void {
    if (tableName) {
      const normalizedTableName = this.normalizeTableName(tableName);
      this.sheetCache.delete(normalizedTableName);
      this.tableDataCache.delete(normalizedTableName);
    } else {
      this.sheetCache.clear();
      this.tableDataCache.clear();
      this.discoveryComplete = false;
    }
    console.log(`[DynamicSheetManager] Cleared cache${tableName ? ` for table: ${tableName}` : ' for all tables'}`);
  }

  // === PRIVATE HELPER METHODS ===

  /**
   * Check if cached sheet info is still valid
   */
  private isCacheValid(sheetInfo: SheetInfo): boolean {
    const age = Date.now() - sheetInfo.lastUpdated.getTime();
    return age < this.CACHE_TTL;
  }

  /**
   * Normalize table name for consistent caching and lookup
   * Unity sends table names like "Topic 1" or "Extras"
   * We use these names directly without modification
   */
  private normalizeTableName(tableName: string): string {
    // Simply trim whitespace and use as-is
    return tableName.trim();
  }

  /**
   * Analyze an existing sheet to extract localization table information
   */
  private async analyzeSheet(sheetName: string, sheetId: number, wasCreated = false, spreadsheetId?: string): Promise<SheetInfo | null> {
    try {
      const actualSpreadsheetId = spreadsheetId || '';
      const values = await googleSheetsService.getSheetValues(
        actualSpreadsheetId,
        sheetName
      );

      if (!values || values.length === 0) {
        console.log(`[DynamicSheetManager] Sheet "${sheetName}" is empty`);
        return null;
      }

      const headers = values[0] || [];
      if (headers.length === 0) {
        console.log(`[DynamicSheetManager] Sheet "${sheetName}" has no headers`);
        return null;
      }

      // Find key column
      const keyColumnIndex = this.findKeyColumn(headers);
      if (keyColumnIndex === -1) {
        console.log(`[DynamicSheetManager] Sheet "${sheetName}" has no recognizable key column`);
        return null;
      }

      // Find language columns
      const languageColumns = this.findLanguageColumns(headers);
      console.log(`[DynamicSheetManager] Found language columns for "${sheetName}":`, Array.from(languageColumns.entries()));

      // Use the sheet name directly as the table name
      const tableName = sheetName;

      const sheetInfo: SheetInfo = {
        tableName,
        spreadsheetId: actualSpreadsheetId,
        sheetName,
        sheetId,
        wasCreated,
        keyColumnIndex,
        languageColumns,
        lastUpdated: new Date()
      };

      return sheetInfo;

    } catch (error) {
      console.error(`[DynamicSheetManager] Failed to analyze sheet "${sheetName}":`, error);
      return null;
    }
  }

  /**
   * Set up headers for a newly created sheet
   */
  private async setupSheetHeaders(spreadsheetId: string, sheetName: string): Promise<void> {
    console.log(`[DynamicSheetManager] Setting up headers for sheet: ${sheetName}`);

    // Try to detect language columns from other sheets first
    let headers: string[] = ['Key'];

    const existingSheets = await this.getAllTables();
    for (const referenceSheet of existingSheets) {
      if (referenceSheet.sheetName === sheetName) {
        continue;
      }

      try {
        const refData = await googleSheetsService.getSheetValues(spreadsheetId, referenceSheet.sheetName, 'A1:Z1');
        const refHeaders = refData[0];

        if (Array.isArray(refHeaders)) {
          const languageHeaders = refHeaders.filter((header: string) =>
            header && header.toLowerCase() !== 'key'
          );

          if (languageHeaders.length > 0) {
            headers = headers.concat(languageHeaders);
            break;
          }
        }
      } catch (error) {
        console.warn(`[DynamicSheetManager] Unable to read headers from reference sheet "${referenceSheet.sheetName}":`, error);
      }
    }

    // If no language columns detected, fall back to configured defaults
    if (headers.length === 1) {
      console.log('[DynamicSheetManager] No language columns detected, using configured defaults');

      const configuredDefaults = GOOGLE_CONFIG.languages?.defaultLanguages ?? [DEFAULT_LANGUAGE_PLACEHOLDER];
      const sanitizedDefaults = Array.from(
        new Set(
          configuredDefaults
            .map(language => (typeof language === 'string' ? language.trim() : ''))
            .filter((language): language is string => language.length > 0)
        )
      );

      const languagesToAdd = sanitizedDefaults.length > 0 ? sanitizedDefaults : [DEFAULT_LANGUAGE_PLACEHOLDER];
      const columnMapping = GOOGLE_CONFIG.languages?.columnMapping as Record<string, string> | undefined;

      for (const languageName of languagesToAdd) {
        const mappedName = columnMapping?.[languageName] || languageName;
        if (!headers.includes(mappedName)) {
          headers.push(mappedName);
        }
      }
    }

    const updateCells = headers.map((header, index) => ({
      row: 1,
      col: index,
      value: header
    }));

    await googleSheetsService.batchUpdateSheet(spreadsheetId, sheetName, updateCells);

    console.log(`[DynamicSheetManager] Set up headers: ${headers.join(', ')}`);
  }

  /**
   * Find the key column in the headers
   */
  private findKeyColumn(headers: string[]): number {
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]?.toLowerCase().trim();
      if (header === 'key') {
        return i;
      }
    }

    // If no "key" column found, assume first column
    return headers.length > 0 ? 0 : -1;
  }

  /**
   * Find language columns in the headers
   */
  private findLanguageColumns(headers: string[]): Map<string, number> {
    const languageColumns = new Map<string, number>();
    const keyColumnIndex = this.findKeyColumn(headers);

    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]?.toLowerCase().trim();
      if (!header || i === keyColumnIndex) continue;

      // Any non-key column is treated as a language column
      languageColumns.set(header, i);
    }

    return languageColumns;
  }

}

// Export singleton instance
export const dynamicSheetManager = DynamicSheetManagerService.getInstance();
